import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { LanguageSelector } from './LanguageSelector'
import { LocalizedLink } from './LocalizedLink'
import { cn } from '@/lib/utils'
import type { RouteKey } from '@/i18n/routes'
import { localizedRoutes } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface NavItem {
  label: string
  to: RouteKey
  children?: { label: string; to: RouteKey }[]
}

function useScrolled(threshold = 50) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [threshold])
  return scrolled
}

function NavDropdown({
  item,
}: {
  item: NavItem
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [containerRef])

  return (
    <li ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'group flex items-center gap-1 px-4 py-2 text-sm font-medium transition-all duration-300',
          'text-white/80 hover:text-white'
        )}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {item.label}
        <ChevronDown
          className={cn(
            'h-4 w-4 transition-transform duration-300',
            open && 'rotate-180',
            'text-white/50'
          )}
        />
      </button>
      {open && (
        <ul
          className="absolute left-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-lg border border-[var(--color-graphite)] bg-[var(--color-ink)]/95 py-2 shadow-xl backdrop-blur-xl"
          role="menu"
        >
          {item.children!.map((child) => (
            <li key={child.to} role="none">
              <LocalizedLink
                to={child.to}
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--color-fog)] transition-all hover:bg-[var(--color-charcoal)] hover:text-[var(--accent-primary)]"
                role="menuitem"
              >
                <span className="h-1 w-1 rounded-full bg-[var(--color-steel)] transition-colors group-hover:bg-[var(--accent-primary)]" />
                {child.label}
              </LocalizedLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function useIsActive(routeKey: RouteKey) {
  const location = useLocation()
  const { i18n } = useTranslation()
  const lang = i18n.language as SupportedLanguage
  const routePath = localizedRoutes[routeKey]?.[lang]
  const expectedPath = routePath ? `/${lang}/${routePath}` : `/${lang}`
  return location.pathname === expectedPath || location.pathname === expectedPath + '/'
}

function NavLink({ item }: { item: NavItem }) {
  const isActive = useIsActive(item.to)
  return (
    <li key={item.to}>
      <LocalizedLink
        to={item.to}
        className={cn(
          'relative px-4 py-2 text-sm font-medium transition-all duration-300',
          'text-white/80 hover:text-white',
          isActive && 'text-white'
        )}
      >
        {item.label}
        {isActive && (
          <span className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full bg-[var(--accent-primary)]" />
        )}
      </LocalizedLink>
    </li>
  )
}

export function Header() {
  const { t } = useTranslation()
  const isScrolled = useScrolled(50)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)

  const navItems: NavItem[] = [
    { label: t('nav.home'), to: 'home' },
    { label: t('nav.about'), to: 'chiSiamo' },
    { label: t('nav.sectors'), to: 'sectors' },
    { label: t('nav.processes'), to: 'processes' },
    { label: t('nav.news'), to: 'news' },
    { label: t('nav.careers'), to: 'careers' },
  ]

  return (
    <header
      className={cn(
        'fixed left-0 right-0 top-0 z-[200] transition-all duration-500',
        isScrolled
          ? 'bg-[var(--color-void)]/90 shadow-lg shadow-black/20 backdrop-blur-xl'
          : 'bg-transparent'
      )}
    >
      {/* Skip Link */}
      <a
        href="#main-content"
        className="absolute left-0 top-0 z-[201] -translate-y-full bg-[var(--accent-primary)] px-4 py-2 text-sm font-semibold text-white transition-transform focus:translate-y-0"
      >
        {t('aria.skipToContent')}
      </a>

      <div className="container">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <LocalizedLink to="home" className="flex items-center gap-3">
            <div className="relative">
              <img
                src="/media/logos/logo.png"
                alt="FG Automazioni"
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <div className="hidden border-l border-white/20 pl-3 text-sm font-medium text-white/60 sm:block">
              <span className="block text-xs font-normal opacity-60">
                {t('header.tagline').split(/\s+/)[0]}
              </span>
              {t('header.tagline').split(/\s+/).slice(1).join(' ')}
            </div>
          </LocalizedLink>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            <ul className="flex items-center">
              {navItems.map((item) =>
                item.children ? (
                  <NavDropdown key={item.to} item={item} />
                ) : (
                  <NavLink key={item.to} item={item} />
                )
              )}
            </ul>

            {/* Divider + Language */}
            <div className="ml-4 border-l border-white/10 pl-4">
              <LanguageSelector variant="desktop" isScrolled={isScrolled} />
            </div>

            {/* CTA Button */}
            <LocalizedLink
              to="contact"
              className="ml-6 rounded-md bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20"
            >
              {t('buttons.contactUs')}
            </LocalizedLink>
          </nav>

          {/* Mobile controls */}
          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSelector variant="desktop" isScrolled={isScrolled} />
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-white transition-all duration-300 hover:bg-white/10"
              aria-label={mobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          'bg-[var(--color-void)] overflow-hidden transition-all duration-500 lg:hidden',
          mobileOpen ? 'max-h-[800px] border-t border-[var(--color-graphite)]' : 'max-h-0'
        )}
      >
        <div className="container pt-6 pb-6">
          <ul className="space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <li key={item.to} className="border-b border-[var(--color-graphite)] pb-2">
                  <button
                    onClick={() =>
                      setMobileExpanded((e) => (e === item.to ? null : item.to))
                    }
                    className="flex w-full items-center justify-between py-3 text-sm font-medium text-[var(--color-fog)]"
                    aria-expanded={mobileExpanded === item.to}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-[var(--color-steel)] transition-transform duration-300',
                        mobileExpanded === item.to && 'rotate-180'
                      )}
                    />
                  </button>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300',
                      mobileExpanded === item.to ? 'max-h-40' : 'max-h-0'
                    )}
                  >
                    <ul className="mt-2 space-y-1 border-l-2 border-[var(--color-graphite)] pl-4">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <LocalizedLink
                            to={child.to}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2 text-sm text-[var(--color-ash)] transition-colors hover:text-[var(--accent-primary)]"
                          >
                            {child.label}
                          </LocalizedLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ) : (
                <li key={item.to} className="border-b border-[var(--color-graphite)]">
                  <LocalizedLink
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-medium text-[var(--color-fog)] transition-colors hover:text-[var(--accent-primary)]"
                  >
                    {item.label}
                  </LocalizedLink>
                </li>
              )
            )}
          </ul>

          {/* Mobile CTA */}
          <div className="mt-6 mb-8">
            <LocalizedLink
              to="contact"
              onClick={() => setMobileOpen(false)}
              className="flex w-full items-center justify-center rounded-lg bg-[var(--accent-primary)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--color-copper-400)]"
            >
              {t('buttons.contactUs')}
            </LocalizedLink>
          </div>

        </div>
      </div>
    </header>
  )
}
